import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { Camera, Search } from 'lucide-react';
import { normalizeBarcode, useTranslation } from '@dukanti/shared';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { BarcodeCameraScanDialog } from './barcode-camera-scan-dialog';

export interface BarcodeScanInputHandle {
  focus: () => void;
}

interface BarcodeScanInputProps {
  readonly value?: string;
  readonly onChange?: (value: string) => void;
  readonly disabled?: boolean;
  readonly pending?: boolean;
  readonly defaultValue?: string;
  readonly initialCameraOpen?: boolean;
  readonly onSubmit: (rawBarcode: string) => void;
}

export const BarcodeScanInput = forwardRef<BarcodeScanInputHandle, BarcodeScanInputProps>(
  function BarcodeScanInput(
    {
      value: controlledValue,
      onChange,
      disabled = false,
      pending = false,
      defaultValue = '',
      initialCameraOpen = false,
      onSubmit,
    },
    ref,
  ) {
    const { t } = useTranslation();
    const [internalValue, setInternalValue] = useState(defaultValue);
    const [cameraOpen, setCameraOpen] = useState(initialCameraOpen);
    const value = controlledValue ?? internalValue;
    const inputRef = useRef<HTMLInputElement | null>(null);

    useImperativeHandle(ref, () => ({
      focus: () => inputRef.current?.focus(),
    }));

    const submit = () => {
      const normalized = normalizeBarcode(value);
      if (normalized.length === 0) return;
      onSubmit(value);
    };

    const applyCameraResult = (barcode: string) => {
      setInternalValue(barcode);
      onChange?.(barcode);
      onSubmit(barcode);
    };

    return (
      <section className="border-border bg-card shadow-admin-card rounded-lg border p-4">
        <Label
          htmlFor="barcode-scan-input"
          className="text-foreground text-start text-sm font-medium"
        >
          {t('admin.barcode.scan.inputLabel')}
        </Label>
        <div className="mt-2 flex flex-col gap-2 sm:flex-row">
          <Input
            ref={inputRef}
            id="barcode-scan-input"
            value={value}
            onChange={(event) => {
              setInternalValue(event.target.value);
              onChange?.(event.target.value);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                submit();
              }
            }}
            disabled={disabled || pending}
            autoFocus
            autoComplete="off"
            autoCapitalize="off"
            inputMode="text"
            dir="ltr"
            className="text-start"
            placeholder={t('admin.barcode.scan.inputPlaceholder')}
            aria-describedby="barcode-scan-hint"
            data-testid="barcode-scan-input"
          />
          <Button
            type="button"
            onClick={submit}
            disabled={disabled || pending || normalizeBarcode(value).length === 0}
            data-testid="barcode-scan-submit"
          >
            <Search className="me-2 size-4" aria-hidden />
            {t('admin.barcode.scan.submit')}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => setCameraOpen(true)}
            disabled={disabled || pending}
            data-testid="barcode-camera-open"
            aria-label={t('admin.barcode.scan.cameraOpen')}
          >
            <Camera className="me-2 size-4" aria-hidden />
            {t('admin.barcode.scan.cameraOpen')}
          </Button>
        </div>
        <p id="barcode-scan-hint" className="text-muted-foreground mt-2 text-start text-xs">
          {pending ? t('common.loading') : t('admin.barcode.scan.normalizedHint')}
        </p>
        <BarcodeCameraScanDialog
          open={cameraOpen}
          onOpenChange={setCameraOpen}
          onDetected={applyCameraResult}
        />
      </section>
    );
  },
);
